export const DOC_NAME: Record<string, string> = {
  BEERS2023: "BEERS",
  SPSTv3: "SPST",
  SPSTv2: "SPST",
  CCMM: "CCMM",
  LCMM: "LCMM",
  POLY: "다제",
  KABS: "KABS",
  KDGERI: "안전원",
};

export type DocumentType = keyof typeof DOC_NAME | "LABEL" | "HFF";

// 참고문헌 ID 패턴에 따라 문서 타입을 결정하는 함수
export const getDocumentTypeFromRefId = (refId: string): DocumentType => {
  // HFF_ 접두사: 건강기능식품 참고문헌 (예: HFF_NB_12345, HFF_UD_12345)
  if (refId.startsWith("HFF_")) {
    return "HFF";
  }

  // 전체 ID 패턴에서 BEERS2023이 포함된 경우 또는 BEERS_로 시작하는 경우
  if (refId.includes("BEERS2023") || refId.startsWith("BEERS_")) {
    return "BEERS2023";
  }

  // 새로운 SPST ID 체계 (STOP_, START_로 시작하는 경우)
  if (refId.startsWith("STOP_") || refId.startsWith("START_")) {
    return "SPSTv3";
  }

  // 전체 ID 패턴에서 SPSTv3나 SPST가 포함된 경우 (기존 호환성)
  if (refId.includes("SPSTv3") || refId.includes("SPST")) {
    return "SPSTv3";
  }

  // 전체 ID 패턴에서 CCMM이 포함된 경우
  if (refId.includes("CCMM")) {
    return "CCMM";
  }

  // 전체 ID 패턴에서 LCMM이 포함된 경우
  if (refId.includes("LCMM")) {
    return "LCMM";
  }

  // [2025-01-20] 결정사항: POLY 관련 refid 패턴들을 모두 POLY로 분류
  // drug_AC_ (복약 이행 관리), FMM_ (고위험 약물), edu_ (교육자료) 패턴들
  if (refId.startsWith("AC_") || refId.startsWith("FMM_") || refId.startsWith("edu_")) {
    return "POLY";
  }

  // 전체 ID 패턴에서 POLY가 포함된 경우
  if (refId.includes("POLY")) {
    return "POLY";
  }

  // 전체 ID 패턴에서 KABS가 포함된 경우 또는 항콜린성 관련 패턴
  if (refId.includes("KABS") || refId.startsWith("ANTICHOLINERGIC") || refId.startsWith("ACS_")) {
    return "KABS";
  }

  // 전체 ID 패턴에서 KDGERI가 포함된 경우 또는 KDGERI_로 시작하는 경우
  if (refId.includes("KDGERI") || refId.startsWith("KDGERI_")) {
    return "KDGERI";
  }

  // 축약형 ID들 - 콘솔에서 확인된 패턴들
  // AC: 기존에는 CCMM으로 분류했지만, drug_AC_ 패턴과 구분하여 처리
  if (refId.startsWith("AC") && !refId.startsWith("AC_")) {
    return "CCMM";
  }

  // SPST: STOPP&START (기존 호환성)
  if (refId.startsWith("SPST")) {
    return "SPSTv3";
  }

  // PLY: 다제 관련 참고문헌
  if (refId.startsWith("PLY")) {
    return "POLY";
  }

  // PRD1, DDIN, STOR: 약물 데이터베이스 관련 (허가사항으로 분류)
  if (refId.startsWith("PRD1") || refId.startsWith("DDIN") || refId.startsWith("STOR")) {
    return "LABEL";
  }

  // 추가 허가사항 관련 패턴들
  // NB (주의사항), EE (효능효과), UD (용법용량), INGREDI (성분), ACTN (약리작용), EXPR (전문가를 위한 정보)
  if (
    refId.startsWith("NB") ||
    refId.startsWith("EE") ||
    refId.startsWith("UD") ||
    refId.startsWith("INGREDI") ||
    refId.startsWith("ACTN") ||
    refId.startsWith("EXPR") ||
    refId.startsWith("ELSE") ||
    refId.startsWith("UNDEF")
  ) {
    return "LABEL";
  }

  // 하위 모듈 패턴들 (주의사항의 하위 분류)
  if (
    refId.startsWith("WARN") ||
    refId.startsWith("CONT") ||
    refId.startsWith("CARE") ||
    refId.startsWith("SIDE") ||
    refId.startsWith("PRCU") ||
    refId.startsWith("OVER") ||
    refId.startsWith("ADMN") ||
    refId.startsWith("CLIX") ||
    refId.startsWith("KDNY") ||
    refId.startsWith("LIVR") ||
    refId.startsWith("PRD") ||
    refId.startsWith("DEFAULT")
  ) {
    return "LABEL";
  }

  // 기본값: 허가사항
  return "LABEL";
};

// 탭명을 위한 함수 (EvidenceSection에서 사용)
export const getTabNameFromDocType = (docType: DocumentType): string | undefined => {
  if (docType === "LABEL") return "허가사항";
  if (docType === "HFF") return "건기식";
  return DOC_NAME[docType];
};
